/**
 * Per-group default layouts (§2.11 "Layouts"): new users start from their group's default — advocacy 67,
 * resident 69, office 70. Reset goes to the team layout, or to this default when there isn't one.
 * Cells are measured from the 1440px screenshots (12 × 80px columns, 16px gaps, 72px rows).
 */
import type { UserGroup } from "../nav/navConfig";
import { defaultSettings, type ChartSettings, type DataBinding } from "./charts/chartSettings";
import type { ChartType } from "./charts/chartTypes";
import type { LayoutItem, MetricId, WidgetConfig, WidgetType } from "./types";

const item = (id: string, type: WidgetType, x: number, y: number, w: number, h: number, config: WidgetConfig = {}): LayoutItem =>
  ({ id, type, x, y, w, h, config });

/** A chart / table widget's settings: the chart settings popup's defaults plus this binding. */
export function chartSettings(type: ChartType, data: Partial<DataBinding> & Pick<DataBinding, "datasetId">, title?: string): ChartSettings {
  const s = defaultSettings(type, data.datasetId);
  return { ...s, data: { ...s.data, ...data }, labels: { ...s.labels, title } };
}

const metric = (id: string, x: number, metricId: MetricId): LayoutItem => item(id, "number", x, 0, 3, 2, { metric: metricId });

/** Lobbyist / advocacy (screenshots/67). */
const advocacy: LayoutItem[] = [
  item("next-vote", "nextVote", 0, 0, 3, 2),
  item("tossups-s", "tossUps", 3, 0, 3, 2, { title: "Toss-ups on your items" }),
  metric("forecast-accuracy", 6, "forecastAccuracy"),
  metric("new-filings", 9, "newFilings"),
  item("forecast-watch", "forecastWatch", 0, 2, 6, 4),
  item("tossups", "tossUps", 6, 2, 6, 4),
  item("caa-by-alder", "chart", 0, 6, 8, 4, {
    title: "CAA contributions by alderperson", meta: "Contributions · since 2019", sources: "all",
    settings: chartSettings("bar", { datasetId: "contributions", mapping: { category: "recipient", value: { field: "amount", agg: "sum" }, tooltip: [] },
      filters: [{ field: "client", op: "is", value: "chicagoland-apartment-association" }], topN: 8, groupRest: false }, "CAA contributions by alderperson"),
  }),
  item("targets", "targetList", 8, 6, 4, 4),
  item("housing-clients", "table", 0, 10, 6, 3, {
    title: "Top lobbying clients on housing", meta: "Lobbying · since 2019",
    settings: chartSettings("table", { datasetId: "organizations", mapping: { category: "name", value: { field: "items", agg: "sum" }, tooltip: ["filings", "toAlderpersons"] },
      filters: [{ field: "topic", op: "is", value: "housing" }], sort: "valueDesc", topN: 4, groupRest: false }, "Top lobbying clients on housing"),
  }),
  item("opposition", "oppositionWatch", 6, 10, 6, 3),
];

/** Resident (screenshots/69). */
const resident: LayoutItem[] = [
  item("next-vote", "nextVote", 0, 0, 3, 2),
  item("alderperson", "yourAlderperson", 3, 0, 6, 2),
  metric("weighed-in", 9, "weighedIn"),
  item("how-would-you-vote", "howWouldYouVote", 0, 2, 6, 4),
  item("alder-votes", "alderVotes", 6, 2, 6, 4, { title: "Your alderperson's recent votes", meta: "divided votes" }),
  item("pre-meeting-money", "chart", 0, 6, 6, 4, {
    title: "Ald. {alderperson} fundraising before council meetings", meta: "Money · ISBE", note: "Raised in the 30 days before a council meeting, vs. their usual pace",
    settings: chartSettings("column", { datasetId: "contributions", mapping: { category: "date", value: { field: "amount", agg: "sum" }, tooltip: [] },
      filters: [{ field: "window", op: "is", value: "preMeeting" }], sort: "none", groupRest: false, followViewer: "ward" }),
  }),
  item("since", "sinceLastVisit", 6, 6, 6, 4),
  item("neighbors", "neighborsSpoke", 0, 10, 8, 3),
  item("tension", "councilTension", 8, 10, 4, 3),
];

/** Alderperson's office (screenshots/70). */
const office: LayoutItem[] = [
  item("constituents", "constituentsSpoke", 0, 0, 3, 2),
  item("ward-responses", "wardSaid", 3, 0, 3, 2),
  item("tossups-s", "tossUps", 6, 0, 3, 2, { title: "Toss-ups", meta: "{item}" }),
  metric("lobbyists", 9, "lobbyistsOnItems"),
  item("ward-said", "chart", 0, 2, 6, 4, {
    title: "Your ward said", meta: "Ward responses",
    settings: chartSettings("stacked", { datasetId: "wardResponses", mapping: { category: "item", value: { agg: "count" }, split: "stance", tooltip: [] },
      filters: [], sort: "none", groupRest: false, followViewer: "ward" }, "Your ward said"),
  }),
  item("whip", "whipCount", 6, 2, 6, 4),
  item("testimony", "chart", 0, 6, 8, 4, {
    title: "Constituent testimony by topic", meta: "Meetings · this term",
    settings: chartSettings("column", { datasetId: "testimony", mapping: { category: "topic", value: { agg: "count" }, tooltip: [] },
      filters: [], sort: "valueDesc", topN: 6, groupRest: true, followViewer: "ward" }, "Constituent testimony by topic"),
  }),
  item("allies", "allies", 8, 6, 4, 4, { meta: "this term" }),
  item("lobbying", "lobbyingOnItems", 0, 10, 6, 3, { title: "Table · Lobbying on your items", meta: "filings · testimony", icon: "table" }),
  item("meeting-log", "meetingLog", 6, 10, 6, 3),
];

export const DEFAULT_LAYOUTS: Record<UserGroup, LayoutItem[]> = { advocacy, resident, office };

/** A fresh copy (layouts are edited in place by the store's reducers). */
export const groupDefault = (group: UserGroup): LayoutItem[] => structuredClone(DEFAULT_LAYOUTS[group]);
